import { useRef } from 'react';

import {
  type NWTEvent,
  type PageViewTrackingEvent,
  type InteractionTrackingEvent,
  useTracking,
} from '@xing-com/crate-communication-tracking';

type AdobeTrackingEvent<T = Record<string, unknown>> =
  InteractionTrackingEvent<T>;

export type TrackParams = {
  itemUrn?: string;
  trackingToken?: string;
  status: 'TakenJob' | 'AvailableJob' | 'Tutorial';
  cardType: 'tutorial' | 'wildcard' | 'job_reco';
};

export type trackTileInteractionParams = {
  tileName: 'skills' | 'salary' | 'commonalities' | 'company';
  status: 'TakenJob' | 'AvailableJob' | 'Tutorial';
  itemUrn?: string;
  trackingToken?: string;
};

type DreamTrackingType = {
  trackInView: (params: TrackParams) => void;
  trackLike: (params: TrackParams) => void;
  trackDislike: (params: TrackParams) => void;
  trackSuperLike: (params: TrackParams) => void;
  trackTileInteraction: (params: trackTileInteractionParams) => void;
  trackAdobeAction: (action: string) => void;
  trackInfoClick: () => void;
  trackOpenCompany: (params: TrackParams) => void;
  trackProfileClickOnTileCommonalities: (
    params: trackTileInteractionParams
  ) => void;
  trackSeeJobAb: (params: TrackParams) => void;
  trackBreatherView: (elementDetail: string) => void;
  trackNwtOpened: (params: { element: string; elementDetail: string }) => void;
  trackSkillsAddToProfile: (params: TrackParams, countSelected: number) => void;
  trackSkillsAddWithDialog: (
    params: TrackParams,
    countSelected: number
  ) => void;
  trackSkillsAddedSuccess: (hadSkillsAlready: boolean) => void;
  trackSkillsAddedFail: () => void;
  trackUpsellSeen: () => void;
  trackUpsellCta: () => void;
  trackUpsellSkip: () => void;
  trackUpsellClose: () => void;
};

const useDreamTracking = (): DreamTrackingType => {
  const { track: trackPageView } = useTracking<PageViewTrackingEvent>();
  const { track: trackAdobe } = useTracking<AdobeTrackingEvent>();
  const { track: trackNwt } = useTracking<NWTEvent>();
  const trackedInView = useRef<string[]>([]);
  const nwtPage = 'jobs/dream/job_card';

  // card entered viewport
  const trackInView = ({
    itemUrn,
    trackingToken,
    status,
  }: TrackParams): void => {
    if (trackedInView.current.length === 0) {
      // first tracked item
      trackNwt({
        type: 'nwt',
        event: 'viewed_screen',
        eventSchema: 'basic',
        sentBy: 'jobs',
        page: 'jobs/dream/job_card',
      });
      trackPageView({
        type: 'pageview',
        channel: 'wbm/stellenmarkt',
        page: 'wbm/stellenmarkt/dream/job_card',
      });
    }
    if (itemUrn && !trackedInView.current.includes(itemUrn)) {
      trackNwt({
        type: 'nwt',
        event: 'entered_viewport',
        eventSchema: 'basic',
        sentBy: 'jobs',
        page: 'jobs/dream/job_card',
        element: 'job_reco_card',
        elementDetail: 'dream_job_reco',
        context: `dream_job_status_${status === 'AvailableJob' ? 'available' : 'taken'}`, //todo: change this to allow availble jobs
        itemUrn,
        trackingToken,
      });
      trackedInView.current.push(itemUrn);
    }
  };

  // interaction tracking
  const trackLike = ({
    itemUrn,
    trackingToken,
    status,
    cardType,
  }: TrackParams): void => {
    if (cardType === 'job_reco') {
      trackNwt({
        type: 'nwt',
        event: 'liked',
        eventSchema: 'basic',
        element: 'job_reco_card',
        elementDetail: 'dream_job_reco',
        context: `dream_job_status_${status === 'AvailableJob' ? 'available' : 'taken'}`,
        sentBy: 'jobs',
        page: 'jobs/dream/job_card',
        itemUrn,
        trackingToken,
      });
      trackAdobe({
        type: 'interaction',
        event: 'EventLike',
        EventLike: '1',
        PropContextDimension1: 'jobs_dream_card_type_job_reco',
        PropContextDimension2: `jobs_dream_job_status_${status === 'AvailableJob' ? 'available' : 'taken'}`,
        PropInteractionType: `jobs_like`,
      });
    } else if (cardType === 'tutorial') {
      trackNwt({
        type: 'nwt',
        event: 'liked',
        eventSchema: 'basic',
        element: cardType + '_card',
        elementDetail: 'dream_job_reco',
        sentBy: 'jobs',
        page: 'jobs/dream/job_card',
        itemUrn,
        trackingToken,
      });
      trackAdobe({
        type: 'interaction',
        event: 'EventLike',
        EventLike: '1',
        PropContextDimension1: `jobs_dream_card_type_${cardType}`,
        PropInteractionType: `dream_tutorial_like`,
      });
    }
  };
  const trackDislike = ({
    itemUrn,
    trackingToken,
    status,
    cardType,
  }: TrackParams): void => {
    if (cardType === 'job_reco') {
      trackNwt({
        type: 'nwt',
        event: 'discarded',
        eventSchema: 'basic',
        element: 'job_reco_card',
        elementDetail: 'dream_job_reco',
        sentBy: 'jobs',
        page: 'jobs/dream/job_card',
        context: `dream_job_status_${status === 'AvailableJob' ? 'available' : 'taken'}`,
        itemUrn,
        trackingToken,
      });
      trackAdobe({
        type: 'interaction',
        event: 'PropTrackAction',
        PropTrackAction: 'jobs_reco_delete',
        PropContextDimension1: 'jobs_dream_card_type_job_reco',
        PropContextDimension2: `jobs_dream_job_status_${status === 'AvailableJob' ? 'available' : 'taken'}`,
      });
    } else {
      trackNwt({
        type: 'nwt',
        event: 'discarded',
        eventSchema: 'basic',
        element: cardType + '_card',
        elementDetail: 'dream_job_reco',
        sentBy: 'jobs',
        page: 'jobs/dream/job_card',
        itemUrn,
        trackingToken,
      });
      trackAdobe({
        type: 'interaction',
        event: 'PropTrackAction',
        PropTrackAction: 'jobs_reco_delete',
        PropContextDimension1: `jobs_dream_card_type_${cardType}`,
      });
    }
  };
  const trackSuperLike = ({
    itemUrn,
    trackingToken,
    status,
    cardType,
  }: TrackParams): void => {
    if (cardType === 'job_reco') {
      if (status === 'AvailableJob') {
        trackAdobe({
          type: 'interaction',
          event: 'EventBookmark',
          EventBookmark: '1',
          PropContextDimension1: 'jobs_dream_card_type_job_reco',
          PropContextDimension2: 'jobs_dream_job_status_available',
          PropInteractionType: 'jobs_bookmark',
        });
        trackNwt({
          type: 'nwt',
          event: 'bookmarked',
          eventSchema: 'basic',
          element: 'job_posting',
          elementDetail: 'dream_job_reco',
          context: `dream_job_status_available`,
          sentBy: 'jobs',
          page: 'jobs/dream/job_card',
          itemUrn,
          trackingToken,
        });
      } else {
        trackAdobe({
          type: 'interaction',
          event: 'PropTrackAction',
          PropContextDimension1: 'jobs_dream_card_type_job_reco',
          PropContextDimension2: 'jobs_dream_job_status_taken',
          PropInteractionType: 'jobs_search_alert_create_confirm',
        });
        trackNwt({
          type: 'nwt',
          event: 'activated',
          eventSchema: 'basic',
          element: 'search_alert',
          elementDetail: 'dream_job_reco',
          context: `dream_job_status_taken`,
          sentBy: 'jobs',
          page: 'jobs/dream/job_card',
          itemUrn,
          trackingToken,
        });
      }
    }
  };

  const trackInfoClick = (): void => {
    trackAdobeAction('jobs_dream_tutorial_click');
    trackNwtOpened({
      element: 'info_button',
      elementDetail: 'header_item',
    });
  };

  const trackTileInteraction = ({
    tileName,
    status,
    itemUrn,
    trackingToken,
  }: trackTileInteractionParams): void => {
    trackAdobe({
      type: 'interaction',
      event: 'PropTrackAction',
      PropTrackAction: `jobs_dream_${tileName}_tile_clicked`,
      PropContextDimension1: 'jobs_dream_card_type_job_reco',
      PropContextDimension2: `jobs_dream_job_status_${status === 'AvailableJob' ? 'available' : 'taken'}`,
    });
    trackNwt({
      type: 'nwt',
      event: 'clicked',
      eventSchema: 'basic',
      sentBy: 'jobs',
      page: nwtPage,
      element: `${tileName}_tile`,
      elementDetail: 'dream_job_reco',
      itemUrn,
      trackingToken,
      context: `dream_job_status_${status === 'AvailableJob' ? 'available' : 'taken'}`,
    });
  };

  const trackAdobeAction = (action: string): void => {
    trackAdobe({
      type: 'interaction',
      event: 'PropTrackAction',
      PropTrackAction: action,
    });
  };

  const trackProfileClickOnTileCommonalities = ({
    itemUrn,
    trackingToken,
    status,
  }: trackTileInteractionParams): void => {
    trackNwt({
      type: 'nwt',
      event: 'opened',
      eventSchema: 'basic',
      sentBy: 'jobs',
      page: nwtPage,
      element: 'dream_job_contact_recommendation',
      elementDetail: 'dream_job_reco',
      context: `dream_job_status_${status === 'AvailableJob' ? 'available' : 'taken'}`,
      itemUrn,
      trackingToken,
    });
  };

  const trackOpenCompany = ({
    itemUrn,
    trackingToken,
    status,
  }: TrackParams): void => {
    trackNwt({
      type: 'nwt',
      event: 'opened',
      eventSchema: 'basic',
      sentBy: 'jobs',
      page: nwtPage,
      element: 'company_logo_name',
      elementDetail: 'dream_job_reco',
      context: `dream_job_status_${status === 'AvailableJob' ? 'available' : 'taken'}`,
      itemUrn,
      trackingToken,
    });
  };

  const trackSeeJobAb = ({
    itemUrn,
    trackingToken,
    status,
  }: TrackParams): void => {
    trackNwt({
      type: 'nwt',
      event: 'opened',
      eventSchema: 'basic',
      sentBy: 'jobs',
      page: nwtPage,
      element: 'job_ad',
      elementDetail: 'dream_job_reco',
      context: `dream_job_status_${status === 'AvailableJob' ? 'available' : 'taken'}`,
      itemUrn,
      trackingToken,
    });
  };

  const trackBreatherView = (elementDetail: string): void => {
    trackNwt({
      type: 'nwt',
      event: 'viewed',
      eventSchema: 'basic',
      sentBy: 'jobs',
      page: 'jobs/dream/job_card',
      element: 'modal',
      elementDetail,
      // trackingToken,
    });
  };

  const trackNwtOpened = ({
    element,
    elementDetail,
  }: {
    element: string;
    elementDetail: string;
  }): void => {
    trackNwt({
      type: 'nwt',
      event: 'opened',
      eventSchema: 'basic',
      sentBy: 'jobs',
      page: nwtPage,
      element,
      elementDetail,
      // trackingToken,
    });
  };

  const trackSkillsAddToProfile = (
    params: TrackParams,
    countSelected: number
  ): void => {
    trackAdobe({
      type: 'interaction',
      event: 'PropTrackAction',
      PropTrackAction: 'jobs_dream_skills_add_to_profile_clicked',
      PropActionOrigin: 'jobs_dream_skills_add_to_profile_clicked',
      PropContextDimension1: `jobs_dream_card_type_${params.cardType}`,
      PropContextDimension2: `jobs_dream_job_status_${params.status === 'AvailableJob' ? 'available' : 'taken'}`,
      PropContextDimension3: `jobs_dream_skills_selected_${countSelected}`,
    });
    trackNwt({
      type: 'nwt',
      event: 'clicked',
      eventSchema: 'basic',
      sentBy: 'jobs',
      page: nwtPage,
      element: `add_skills_cta`,
      elementDetail: 'dream_job_reco',
      itemUrn: params.itemUrn,
      trackingToken: params.trackingToken,
      context: `dream_job_status_${params.status === 'AvailableJob' ? 'available' : 'taken'}`,
      flags: `skills_selected_${countSelected}`,
    });
  };

  const trackSkillsAddWithDialog = (
    params: TrackParams,
    countSelected: number
  ): void => {
    trackAdobe({
      type: 'interaction',
      event: 'PropTrackAction',
      PropTrackAction: 'jobs_dream_skills_add_to_profile_clicked',
      PropActionOrigin: 'jobs_dream_skills_add_dialog',
      PropContextDimension1: `jobs_dream_card_type_${params.cardType}`,
      PropContextDimension2: `jobs_dream_job_status_${params.status === 'AvailableJob' ? 'available' : 'taken'}`,
      PropContextDimension3: `jobs_dream_skills_selected_${countSelected}`,
    });
    trackNwt({
      type: 'nwt',
      event: 'clicked',
      eventSchema: 'basic',
      sentBy: 'jobs',
      page: nwtPage,
      element: `add_skills_dialog`,
      elementDetail: 'dream_job_reco',
      itemUrn: params.itemUrn,
      trackingToken: params.trackingToken,
      context: `dream_job_status_${params.status === 'AvailableJob' ? 'available' : 'taken'}`,
      flags: `skills_selected_${countSelected}`,
    });
  };

  const trackSkillsAddedSuccess = (hadSkillsAlready: boolean): void => {
    trackAdobe({
      type: 'interaction',
      event: 'EventProfileEditingSave',
      EventProfileEditingSave: 1,
      PropActionOrigin: 'jobs_dream_matching_skills_bottom_sheet',
      PropContextDimension3: hadSkillsAlready
        ? `profile_self_editing_ProfileSkillsModule_edit`
        : `profile_self_editing_ProfileSkillsModule_empty_state_view`,
    });
    trackNwt({
      type: 'nwt',
      event: 'profile_editing_saved',
      eventSchema: 'basic',
      sentBy: 'jobs',
      context: `skills_edit`,
      page: nwtPage,
      flags: `empty_state_${hadSkillsAlready ? 'false' : 'true'}`,
    });
  };

  const trackSkillsAddedFail = (): void => {
    trackAdobe({
      type: 'interaction',
      event: 'PropTrackAction',
      PropTrackAction: 'jobs_dream_add_skills_error',
      PropActionOrigin: 'jobs_dream_matching_skills_bottom_sheet',
    });
    trackNwt({
      type: 'nwt',
      event: 'error_shown',
      eventSchema: 'basic',
      sentBy: 'jobs',
      page: nwtPage,
      context: `skills_edit_error_server_unavailable`,
    });
  };

  const trackUpsellSeen = (): void => {
    trackNwt({
      type: 'nwt',
      event: 'viewed',
      eventSchema: 'basic',
      sentBy: 'jobs',
      page: 'jobs/dream/job_card',
      element: 'upsell_banner',
      elementDetail: 'premium_upsell_banner',
    });
  };

  const trackUpsellCta = (): void => {
    trackNwt({
      type: 'nwt',
      event: 'opened',
      eventSchema: 'basic',
      sentBy: 'jobs',
      page: 'jobs/dream/job_card',
      element: 'get_premium',
      elementDetail: 'premium_upsell_banner',
    });
  };

  const trackUpsellSkip = (): void => {
    trackNwt({
      type: 'nwt',
      event: 'clicked',
      eventSchema: 'basic',
      sentBy: 'jobs',
      page: 'jobs/dream/job_card',
      element: 'skip_offer',
      elementDetail: 'premium_upsell_banner',
    });
  };

  const trackUpsellClose = (): void => {
    trackNwt({
      type: 'nwt',
      event: 'clicked',
      eventSchema: 'basic',
      sentBy: 'jobs',
      page: 'jobs/dream/job_card',
      element: 'close_offer',
      elementDetail: 'premium_upsell_banner',
    });
  };

  return {
    trackInView,
    trackLike,
    trackDislike,
    trackSuperLike,
    trackInfoClick,
    trackTileInteraction,
    trackAdobeAction,
    trackOpenCompany,
    trackProfileClickOnTileCommonalities,
    trackSeeJobAb,
    trackBreatherView,
    trackNwtOpened,
    trackSkillsAddToProfile,
    trackSkillsAddWithDialog,
    trackSkillsAddedSuccess,
    trackSkillsAddedFail,
    trackUpsellSeen,
    trackUpsellCta,
    trackUpsellSkip,
    trackUpsellClose,
  };
};

export { useDreamTracking };
